// src/pages/QuickAssign.tsx
// Mobile-optimized quick assignment page for shift equipment issue
// Phase 4: Mobile Quick Assign Implementation

import React, { useState, useEffect, useMemo } from 'react';
import { 
  ArrowLeft, 
  Search, 
  X, 
  Check, 
  Plus,
  User,
  Package,
  ChevronRight,
  LogOut,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import qmLeoLogo from '../assets/images/qmLEO_logo.png';
import { useTenantConfigurations } from '../hooks/useTenantConfigurations';
import { userAPI } from '../services/userAPI';
import { inventoryAPI, InventoryItem } from '../services/inventoryAPI';
import { assignmentAPI } from '../services/assignmentAPI';
import { User as UserType } from '../types/user';
import { logger } from '../utils/logger';

const quickAssignLogger = logger.createServiceLogger('QuickAssign');

// Types
type Step = 'home' | 'officer' | 'items' | 'confirm' | 'success';

interface SelectedItem {
  item: InventoryItem;
  quantity?: number;
}

interface SessionAssignment {
  officerName: string;
  items: string[];
  timestamp: Date;
}

// Main Component
const QuickAssign: React.FC = () => {
  const { tenantId, userRole, userName, logout } = useAuth();
  const { configs } = useTenantConfigurations(tenantId || undefined);

  // Step state
  const [step, setStep] = useState<Step>('home');
  
  // Selection state
  const [selectedOfficer, setSelectedOfficer] = useState<UserType | null>(null);
  const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([]);
  const [reason, setReason] = useState<string>('Initial Issue');
  const [notes, setNotes] = useState<string>('');
  
  // Data state
  const [officers, setOfficers] = useState<UserType[]>([]);
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  
  // Search state
  const [officerSearch, setOfficerSearch] = useState('');
  const [itemSearch, setItemSearch] = useState('');
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Session history (clears on browser close)
  const [sessionHistory, setSessionHistory] = useState<SessionAssignment[]>([]);

  // Load initial data
  useEffect(() => {
    loadOfficers();
    loadInventory();
  }, [tenantId]);

  const loadOfficers = async () => {
    try {
      quickAssignLogger.info('Loading officers for quick assign');
      const response = await userAPI.getUsers(1, 500, { employmentStatus: ['Active'] });
      setOfficers(response.users || []);
      quickAssignLogger.info('Officers loaded', { count: response.users?.length || 0 });
    } catch (err: any) {
      quickAssignLogger.error('Failed to load officers', { error: err.message });
      setError('Failed to load officers');
    }
  };

  const loadInventory = async () => {
    try {
      quickAssignLogger.info('Loading inventory for quick assign');
      const response = await inventoryAPI.getItems();
      if (response.success && response.data) {
        // Filter to available items only
        const availableItems = response.data.filter((item: InventoryItem) => {
          if ('status' in item) {
            return item.status === 'Available';
          }
          if ('availableQuantity' in item) {
            return item.availableQuantity > 0;
          }
          return false;
        });
        setInventory(availableItems);
        
        quickAssignLogger.info('Inventory loaded', { 
          total: response.data.length,
          available: availableItems.length
        });
      }
    } catch (err: any) {
      quickAssignLogger.error('Failed to load inventory', { error: err.message });
      setError('Failed to load inventory');
    }
  };

  // Filtered officers based on search
  const filteredOfficers = useMemo(() => {
    if (!officerSearch.trim()) return officers;
    const search = officerSearch.toLowerCase();
    return officers.filter(officer => 
      `${officer.firstName} ${officer.lastName}`.toLowerCase().includes(search) ||
      officer.badgeNumber?.toLowerCase().includes(search) ||
      officer.employeeNumber?.toLowerCase().includes(search)
    );
  }, [officers, officerSearch]);

  // Filtered inventory based on search only
  const filteredInventory = useMemo(() => {
    if (!itemSearch.trim()) return inventory;
    
    const search = itemSearch.toLowerCase();
    return inventory.filter(item => 
      item.name.toLowerCase().includes(search) ||
      ('serialNumber' in item && item.serialNumber?.toLowerCase().includes(search)) ||
      item.description?.toLowerCase().includes(search)
    );
  }, [inventory, itemSearch]);

  // Handlers
  const handleSelectOfficer = (officer: UserType) => {
    setSelectedOfficer(officer);
    setStep('items');
    setOfficerSearch('');
    quickAssignLogger.info('Officer selected', { 
      officerId: officer.userId,
      name: `${officer.firstName} ${officer.lastName}` 
    });
  };

  const handleAddItem = (item: InventoryItem) => {
    // Check if already selected
    if (selectedItems.some(si => si.item.itemId === item.itemId)) {
      return;
    }
    setSelectedItems([...selectedItems, { item }]);
    setItemSearch('');
    quickAssignLogger.info('Item added to cart', { itemId: item.itemId, name: item.name });
  };

  const handleRemoveItem = (itemId: string) => {
    setSelectedItems(selectedItems.filter(si => si.item.itemId !== itemId));
    quickAssignLogger.info('Item removed from cart', { itemId });
  };

  const handleSubmitAssignment = async () => {
    if (!selectedOfficer || selectedItems.length === 0) return;
    
    setSubmitting(true);
    setError(null);
    
    try {
      quickAssignLogger.info('Submitting assignment', {
        officerId: selectedOfficer.userId,
        itemCount: selectedItems.length,
        reason
      });

      // Create assignments for each item
      for (const selectedItem of selectedItems) {
        const assignmentData = {
          assignment: {
            inventoryItemId: selectedItem.item.itemId,
            assignedToUserId: selectedOfficer.userId,
            assignmentReason: reason,
            notes: notes || undefined
          }
        };

        const response = await assignmentAPI.createAssignment(assignmentData);
        
        if (!response.success) {
          throw new Error(response.error?.message || `Failed to assign ${selectedItem.item.name}`);
        }
      }

      // Add to session history
      const newAssignment: SessionAssignment = {
        officerName: `${selectedOfficer.lastName}, ${selectedOfficer.firstName.charAt(0)}`,
        items: selectedItems.map(si => si.item.name),
        timestamp: new Date()
      };
      setSessionHistory([newAssignment, ...sessionHistory]);

      quickAssignLogger.info('Assignment completed successfully', {
        officerId: selectedOfficer.userId,
        itemCount: selectedItems.length
      });

      // Move to success step
      setStep('success');
      
      // Refresh inventory to update availability
      loadInventory();
      
    } catch (err: any) {
      quickAssignLogger.error('Assignment failed', { error: err.message });
      setError(err.message || 'Failed to complete assignment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAssignMoreToSameOfficer = () => {
    setSelectedItems([]);
    setNotes('');
    setStep('items');
  };

  const handleNewAssignment = () => {
    setSelectedOfficer(null);
    setSelectedItems([]);
    setReason('Initial Issue');
    setNotes('');
    setStep('home');
  };

  const handleBack = () => {
    switch (step) {
      case 'officer':
        setStep('home');
        setOfficerSearch('');
        break;
      case 'items':
        setStep('officer');
        setItemSearch('');
        break;
      case 'confirm':
        setStep('items');
        break;
      default:
        setStep('home');
    }
  };

  const handleLogout = async () => {
    await logout();
  };

  // Render functions for each step
  const renderHome = () => (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="bg-[#0d1b2a] text-white px-4 py-3 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <img src={qmLeoLogo} alt="qmLEO" className="h-10" />
          <div>
            <p className="text-sm font-medium text-blue-100">Quick Assign</p>
            <p className="text-xs text-blue-300">{userName}</p>
          </div>
        </div>
        <button 
          onClick={handleLogout}
          className="p-2 hover:bg-white/10 rounded-lg"
        >
          <LogOut className="w-6 h-6" />
        </button>
      </div>

      {/* Main Action */}
      <div className="flex-1 p-6 flex flex-col">
        <button
          onClick={() => setStep('officer')}
          className="w-full py-6 bg-blue-600 text-white rounded-xl text-xl font-semibold flex items-center justify-center space-x-3 active:bg-blue-700 shadow-lg"
        >
          <Plus className="w-8 h-8" />
          <span>Assign Item</span>
        </button>

        {/* Session History */}
        {sessionHistory.length > 0 && (
          <div className="mt-8">
            <h2 className="text-lg font-semibold text-gray-700 mb-3">This Session</h2>
            <div className="space-y-2">
              {sessionHistory.map((assignment, index) => (
                <div 
                  key={index}
                  className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg border border-green-200"
                >
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {assignment.items.join(', ')}
                    </p>
                    <p className="text-xs text-gray-600">
                      → {assignment.officerName}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderOfficerSelect = () => (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="bg-[#0d1b2a] text-white px-4 py-3 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <button onClick={handleBack} className="p-2 -ml-2 hover:bg-white/10 rounded-lg">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <img src={qmLeoLogo} alt="qmLEO" className="h-10" />
        </div>
        <h1 className="text-sm font-medium text-blue-100">Select Officer</h1>
      </div>

      {/* Search */}
      <div className="p-4 bg-gray-50 border-b">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name or badge..."
            value={officerSearch}
            onChange={(e) => setOfficerSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg text-lg"
            autoFocus
          />
        </div>
      </div>

      {/* Officer List */}
      <div className="flex-1 overflow-y-auto">
        {filteredOfficers.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <User className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No officers found</p>
          </div>
        ) : (
          <div className="divide-y">
            {filteredOfficers.map(officer => (
              <button
                key={officer.userId}
                onClick={() => handleSelectOfficer(officer)}
                className="w-full p-4 flex items-center space-x-4 hover:bg-gray-50 active:bg-gray-100 text-left"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <User className="w-6 h-6 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900">
                    {officer.lastName}, {officer.firstName}
                  </p>
                  <p className="text-sm text-gray-600">
                    Badge #{officer.badgeNumber} • {officer.rank}
                  </p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );

 const renderItemSelect = () => (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="bg-[#0d1b2a] text-white px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button onClick={handleBack} className="p-2 -ml-2 hover:bg-white/10 rounded-lg">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <img src={qmLeoLogo} alt="qmLEO" className="h-10" />
          </div>
          <div className="text-right">
            <h1 className="text-sm font-medium text-blue-100">Select Items</h1>
            <p className="text-xs text-blue-300">
              For: {selectedOfficer?.lastName}, {selectedOfficer?.firstName}
            </p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="p-4 bg-gray-50 border-b">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search serial #, name, or description..."
            value={itemSearch}
            onChange={(e) => setItemSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg text-lg"
            autoFocus
          />
        </div>
      </div>

      {/* Item List */}
      <div className="flex-1 overflow-y-auto">
        {filteredInventory.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No available items found</p>
            {itemSearch && <p className="text-sm mt-2">Try a different search term</p>}
          </div>
        ) : (
          <div className="divide-y">
            {filteredInventory.map(item => {
              const isSelected = selectedItems.some(si => si.item.itemId === item.itemId);
              return (
                <button
                  key={item.itemId}
                  onClick={() => !isSelected && handleAddItem(item)}
                  disabled={isSelected}
                  className={`w-full p-4 flex items-center space-x-4 text-left ${
                    isSelected ? 'bg-green-50' : 'hover:bg-gray-50 active:bg-gray-100'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    isSelected ? 'bg-green-100' : 'bg-gray-100'
                  }`}>
                    {isSelected ? (
                      <Check className="w-6 h-6 text-green-600" />
                    ) : (
                      <Package className="w-6 h-6 text-gray-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900">{item.name}</p>
                    <p className="text-sm text-gray-600">
                      {'serialNumber' in item && item.serialNumber 
                        ? `SN: ${item.serialNumber}` 
                        : (item.description || item.location || 'Available')
                      }
                    </p>
                  </div>
                  {!isSelected && (
                    <Plus className="w-6 h-6 text-blue-600" />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Selected Items Footer */}
      {selectedItems.length > 0 && (
        <div className="border-t bg-white p-4 space-y-3">
          <div className="flex flex-wrap gap-2">
            {selectedItems.map(si => (
              <div 
                key={si.item.itemId}
                className="flex items-center space-x-2 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm"
              >
                <span className="truncate max-w-32">{si.item.name}</span>
                <button 
                  onClick={() => handleRemoveItem(si.item.itemId)}
                  className="hover:text-blue-900"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          <button
            onClick={() => setStep('confirm')}
            className="w-full py-4 bg-blue-600 text-white rounded-xl text-lg font-semibold active:bg-blue-700"
          >
            Continue ({selectedItems.length} item{selectedItems.length > 1 ? 's' : ''})
          </button>
        </div>
      )}
    </div>
  );

 const renderConfirm = () => (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="bg-[#0d1b2a] text-white px-4 py-3 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <button onClick={handleBack} className="p-2 -ml-2 hover:bg-white/10 rounded-lg">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <img src={qmLeoLogo} alt="qmLEO" className="h-10" />
        </div>
        <h1 className="text-sm font-medium text-blue-100">Confirm Assignment</h1>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Officer */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Assigning To
          </label>
          <div className="p-4 bg-gray-50 rounded-lg flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="font-medium">{selectedOfficer?.lastName}, {selectedOfficer?.firstName}</p>
              <p className="text-sm text-gray-600">Badge #{selectedOfficer?.badgeNumber}</p>
            </div>
          </div>
        </div>

        {/* Items */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Items ({selectedItems.length})
          </label>
          <div className="space-y-2">
            {selectedItems.map(si => (
              <div 
                key={si.item.itemId}
                className="p-3 bg-gray-50 rounded-lg flex items-center space-x-3"
              >
                <Package className="w-5 h-5 text-gray-600" />
                <div className="flex-1">
                  <p className="font-medium">{si.item.name}</p>
                  {'serialNumber' in si.item && si.item.serialNumber && (
                    <p className="text-sm text-gray-600">SN: {si.item.serialNumber}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Reason */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Reason
          </label>
          <select
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full p-3 border border-gray-300 rounded-lg text-lg bg-white"
          >
            {configs.assignmentReasons.map(r => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Notes (optional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add any notes..."
            rows={3}
            className="w-full p-3 border border-gray-300 rounded-lg text-lg resize-none"
          />
        </div>

        {/* Error */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}
      </div>

      {/* Submit Button */}
      <div className="border-t bg-white p-4">
        <button
          onClick={handleSubmitAssignment}
          disabled={submitting}
          className="w-full py-4 bg-blue-600 text-white rounded-xl text-lg font-semibold active:bg-blue-700 disabled:bg-gray-400 flex items-center justify-center space-x-2"
        >
          {submitting ? (
            <>
              <Loader2 className="w-6 h-6 animate-spin" />
              <span>Assigning...</span>
            </>
          ) : (
            <>
              <Check className="w-6 h-6" />
              <span>Confirm Assignment</span>
            </>
          )}
        </button>
      </div>
    </div>
  );

  const renderSuccess = () => (
    <div className="flex flex-col h-full items-center justify-center p-6">
      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
        <Check className="w-10 h-10 text-green-600" />
      </div>
      
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Success!</h1>
      <p className="text-gray-600 text-center mb-8">
        {selectedItems.length} item{selectedItems.length > 1 ? 's' : ''} assigned to {selectedOfficer?.lastName}, {selectedOfficer?.firstName}
      </p>

      <div className="w-full space-y-3">
        <button
          onClick={handleAssignMoreToSameOfficer}
          className="w-full py-4 bg-blue-600 text-white rounded-xl text-lg font-semibold active:bg-blue-700"
        >
          Assign More to {selectedOfficer?.lastName}
        </button>
        
        <button
          onClick={handleNewAssignment}
          className="w-full py-4 bg-gray-100 text-gray-800 rounded-xl text-lg font-semibold active:bg-gray-200"
        >
          New Assignment
        </button>
      </div>
    </div>
  );

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Main render
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {step === 'home' && renderHome()}
      {step === 'officer' && renderOfficerSelect()}
      {step === 'items' && renderItemSelect()}
      {step === 'confirm' && renderConfirm()}
      {step === 'success' && renderSuccess()}
    </div>
  );
};

export default QuickAssign;
